import { Directive, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {FieldsOptions, FormManagerService} from '@sim/form'; // TODO: Try to avoid dependency external to Angular

import { UIListFilters } from './filters';

interface State<T> {
  form?: UntypedFormGroup;
  controls?: { [P in keyof T]: AbstractControl };
}

@Directive()
export abstract class BaseFiltersComponent<T, Z> implements UIListFilters<T>, OnDestroy {
  protected readonly filters$ = new BehaviorSubject<T | undefined>(undefined);

  @Input() set filters(v: T | undefined) {
    if (this.state.controls) {
      for (const p in this.state.controls) {
        if (v && v[p]) {
          const control = this.state.controls[p];
          if (control) {
            control.setValue(v[p]);
          }
        }
      }
    }
    this.filters$.next(v);
  }

  @Output() readonly apply = new EventEmitter<T>();

  readonly state$ = new BehaviorSubject<State<T>>({
    controls: undefined,
    form: undefined,
  });

  get state() {
    return this.state$.value;
  }

  get data(): T | undefined {
    return this.state.form && this.state.form.value;
  }

  protected readonly unsubscription$ = new Subject();

  constructor(
    protected readonly formManager: FormManagerService,
    metaFilter: FieldsOptions<Z>,
  ) {

    const form = this.formManager.makeForm<Z>(metaFilter);
    const controls = this.formManager.getControls<T>(form);

    this.setState({ form, controls });

    form.valueChanges
      .pipe(takeUntil(this.unsubscription$))
      .subscribe((v) => {
        this.apply.emit(v);
      });
  }

  ngOnDestroy() {
    this.unsubscription$.next(null);
    this.unsubscription$.complete();
  }

  protected setState(state: Partial<State<T>>) {
    this.state$.next({ ...this.state, ...state });
  }
}
